// v6-polish — single source of truth for user preferences.
//
// Before v6-polish, language and theme each owned their own localStorage
// key (`stamps:language`, `eye-gallery:theme`) with their own React
// state. That was fine while there were only two preferences, but every
// new toggle would have meant another key, another effect, another
// place to remember to persist on change. This hook consolidates them.
//
// Storage layout — a single JSON object under `stamps:prefs`:
//   {
//     "lang": "plain" | "technical",
//     "theme": "dark" | "light",
//     "loupeEnabled": boolean
//   }
//
// Defaults (audited-favoured surface):
//   - lang: 'plain' (mum-friendly labels first; technical is opt-in)
//   - theme: 'dark' (matches the existing v5/v6 default)
//   - loupeEnabled: false (the loupe is local to EyePreview today — this
//     field is here as the foundation for "remember last used" features)
//
// Hydration: we read localStorage ONCE during the initial useState
// initializer so the first paint already matches the user's stored
// preference. There's no flash of wrong-theme. The useEffect below only
// handles writes; reads never re-fire after mount.
//
// Future preferences (preset last used, recent parameters, etc.) should
// extend the `Preferences` interface here rather than spawning their own
// localStorage key — keeps the storage layout reviewable in one place.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Lang = 'plain' | 'technical';
export type Theme = 'dark' | 'light';

export interface Preferences {
  lang: Lang;
  theme: Theme;
  /** Foundation field — EyePreview currently owns loupeOn locally because
   *  it resets on image swap. Exposed here so the hook has a stable shape
   *  for future "remember last used" features. */
  loupeEnabled: boolean;
}

const STORAGE_KEY = 'stamps:prefs';

const DEFAULT_PREFERENCES: Preferences = {
  lang: 'plain',
  theme: 'dark',
  loupeEnabled: false,
};

/**
 * Read preferences from localStorage. Returns the defaults when the key
 * is missing or the JSON is malformed — never throws, never returns
 * partial data. The runtime shape is validated against DEFAULT_PREFERENCES
 * so a stale value (e.g. someone hand-editing localStorage) is coerced.
 */
function readStoredPreferences(): Preferences {
  if (typeof window === 'undefined') return { ...DEFAULT_PREFERENCES };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return { ...DEFAULT_PREFERENCES };
    }
    const obj = parsed as Record<string, unknown>;
    const lang: Lang = obj.lang === 'technical' ? 'technical' : 'plain';
    const theme: Theme = obj.theme === 'light' ? 'light' : 'dark';
    const loupeEnabled = obj.loupeEnabled === true;
    return { lang, theme, loupeEnabled };
  } catch {
    /* localStorage blocked or JSON corrupt — fall back to defaults */
    return { ...DEFAULT_PREFERENCES };
  }
}

interface PreferencesContextValue {
  prefs: Preferences;
  setLang: (l: Lang) => void;
  setTheme: (t: Theme) => void;
  setLoupeEnabled: (v: boolean) => void;
  /** Convenience — same semantics as the v5 `useTheme().toggle`. */
  toggleTheme: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider(props: { children: ReactNode }) {
  // Initialize from localStorage synchronously so first paint matches the
  // stored theme (no flash of light before dark, etc.).
  const [prefs, setPrefs] = useState<Preferences>(readStoredPreferences);

  // Persist on every change. Single key + single write beats N keys + N
  // effects when the user starts switching rapidly.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* quota exceeded or storage blocked — best-effort */
    }
  }, [prefs]);

  // Apply the theme class to <html> so Tailwind's dark: variants work.
  // Hoisted here (was previously inside useTheme) so theme persistence
  // and the theme class share one source of truth.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', prefs.theme === 'dark');
  }, [prefs.theme]);

  const setLang = useCallback((lang: Lang) => {
    setPrefs((prev) => (prev.lang === lang ? prev : { ...prev, lang }));
  }, []);
  const setTheme = useCallback((theme: Theme) => {
    setPrefs((prev) => (prev.theme === theme ? prev : { ...prev, theme }));
  }, []);
  const setLoupeEnabled = useCallback((loupeEnabled: boolean) => {
    setPrefs((prev) =>
      prev.loupeEnabled === loupeEnabled ? prev : { ...prev, loupeEnabled },
    );
  }, []);
  const toggleTheme = useCallback(() => {
    setPrefs((prev) => ({ ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' }));
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({ prefs, setLang, setTheme, setLoupeEnabled, toggleTheme }),
    [prefs, setLang, setTheme, setLoupeEnabled, toggleTheme],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {props.children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences must be used inside <PreferencesProvider>');
  }
  return ctx;
}